import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Users, Briefcase, Star, ClipboardCheck, Copy, Loader2, ArrowRight } from "lucide-react";

const KPIS = [
  { key: "total_candidates", label: "Candidates", icon: Users, gold: false },
  { key: "active_positions", label: "Active Positions", icon: Briefcase, gold: false },
  { key: "strong_matches", label: "Strong Matches", icon: Star, gold: true },
  { key: "review_queue", label: "In Review Queue", icon: ClipboardCheck, gold: false },
  { key: "duplicates", label: "Duplicate Flags", icon: Copy, gold: false },
  { key: "processing", label: "Processing Now", icon: Loader2, gold: false },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-8 fade-up">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--teal))] font-semibold">Recruiter Workspace</div>
        <h1 className="font-head text-3xl font-semibold tracking-tight mt-1.5">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1.5">Live overview of candidates, positions and AI match activity.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {KPIS.map(({ key, label, icon: Icon, gold }, i) => (
          <div key={key} data-testid={`kpi-${key}`}
            className="card-hover bg-card border border-border rounded-lg p-4 shadow-card fade-up" style={{ animationDelay: `${i * 35}ms` }}>
            <div className="flex items-center justify-between">
              <span className={`h-8 w-8 rounded-md flex items-center justify-center ${gold ? "bg-[hsl(var(--gold))]/12 text-[hsl(var(--gold))]" : "bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))]"}`}>
                <Icon size={16} className={key === "processing" && stats?.processing > 0 ? "animate-spin" : ""} />
              </span>
            </div>
            <div className={`font-head text-3xl font-semibold mt-3 tracking-tight tabular-nums ${gold ? "text-[hsl(var(--gold))]" : "text-foreground"}`}>
              {stats ? stats[key] : <span className="skeleton inline-block h-8 w-10 align-middle" />}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-card border border-border rounded-lg shadow-card">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-head font-semibold text-base">Candidate Pipeline</h3>
          </div>
          <div className="p-5 space-y-1">
            {stats && Object.keys(stats.status_counts || {}).length > 0 ? (
              Object.entries(stats.status_counts).map(([s, n]) => {
                const total = Object.values(stats.status_counts).reduce((a, b) => a + b, 0) || 1;
                return (
                  <div key={s} className="flex items-center gap-3 py-1.5">
                    <span className="capitalize text-sm text-foreground/80 w-44 truncate">{s.replace(/_/g, " ")}</span>
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-[hsl(var(--teal))] rounded-full" style={{ width: `${(n / total) * 100}%` }} />
                    </div>
                    <span className="text-sm font-semibold tabular-nums w-8 text-right">{n}</span>
                  </div>
                );
              })
            ) : <p className="text-sm text-muted-foreground py-6 text-center">No candidates yet.</p>}
          </div>
        </div>

        <div className="rounded-lg p-6 flex flex-col justify-between text-white overflow-hidden relative shadow-card"
          style={{ background: "linear-gradient(135deg, hsl(184 66% 15%), hsl(184 60% 22%))" }}>
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full" style={{ background: "hsl(var(--gold) / 0.14)" }} />
          <div className="relative">
            <h3 className="font-head font-semibold text-lg">Get started</h3>
            <p className="text-white/75 text-sm mt-2 leading-relaxed max-w-md">Add open positions, then bulk-upload résumés to generate explainable, defensible AI matches automatically.</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-6 relative">
            <button data-testid="quick-positions" onClick={() => navigate("/positions")} className="group bg-white text-[hsl(var(--teal))] px-4 py-2 rounded-md text-sm font-semibold hover:bg-white/90 transition-colors inline-flex items-center gap-1.5">Manage Positions <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></button>
            <button data-testid="quick-upload" onClick={() => navigate("/upload")} className="bg-[hsl(var(--gold))] text-[hsl(215,40%,14%)] px-4 py-2 rounded-md text-sm font-semibold hover:opacity-90 transition-opacity">Upload Résumés</button>
          </div>
        </div>
      </div>
    </div>
  );
}
